// New Recipe modal — 3-stage flow.
//
//   Stage 1: Choose — URL import (recommended) vs Start from scratch.
//   Stage 2a (URL): paste a URL, server extracts a preview, user
//                   reviews/edits, then saves.
//   Stage 2b (Scratch): jump straight to the editor with an empty
//                   ingredients + steps list.
//
// The editor (stage 3) is shared. It commits via
// recipes.createWithItems which atomically creates the recipe + all
// its items. Server-side SSRF guards live in recipe_extractor.mjs;
// the client just calls and handles error toasts.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type RecipeImportPreview } from "../../lib/rpc";

type Stage = "choose" | "url" | "editor";

interface NewRecipeProps {
  open: boolean;
  onClose: () => void;
  onCreated: (recipeId: string) => void;
  notify: (msg: string) => void;
  // When set, the modal opens directly in the editor stage with the
  // recipe's existing fields prefilled. Save calls recipes.update +
  // an item diff instead of recipes.createWithItems.
  editRecipeId?: string | null;
}

// Each draft item carries an optional `id` — set for items pulled in
// from an existing recipe (edit mode), absent for ones the user
// added. Save uses presence/absence to decide update vs add.
interface DraftItem { id?: string; text: string; quantity: string }

interface Draft {
  name: string;
  imageUrl: string | null;
  instructions: string[];
  notes: string;
  items: DraftItem[];
  /** How many people this recipe feeds. Stored as a string in the
   *  draft so the input can be empty during edit; parsed to number
   *  on save. Null/empty = unset (auto-scale stays at 1x for this
   *  recipe). */
  serves: string;
}

const EMPTY: Draft = { name: "", imageUrl: null, instructions: [""], notes: "", items: [{ text: "", quantity: "" }], serves: "" };

const SUPPORTED_HINTS = [
  "allrecipes.com",
  "bonappetit.com",
  "nytimes.com/cooking",
  "seriouseats.com",
  "tasteofhome.com",
  "food52.com",
  "epicurious.com",
];

type ImportTab = "url" | "text";

export function NewRecipeModal({ open, onClose, onCreated, notify, editRecipeId = null }: NewRecipeProps) {
  const [stage, setStage] = useState<Stage>("choose");
  const [importTab, setImportTab] = useState<ImportTab>("url");
  const [url, setUrl] = useState("");
  const [pastedText, setPastedText] = useState("");
  const [importing, setImporting] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  // Snapshot of the original items at edit-open time. Used to diff
  // against draft.items on save (which items the user deleted, which
  // they edited, which they added). Empty for create flow.
  const [originalItems, setOriginalItems] = useState<DraftItem[]>([]);

  // Reset state whenever the modal opens fresh. Branches on edit mode:
  //   create → 3-stage flow starting at "choose"
  //   edit   → skip to "editor" with the recipe prefilled.
  useEffect(() => {
    if (!open) return;
    setImportTab("url");
    setUrl("");
    setPastedText("");
    setImporting(false);
    setBlockedUrl(null);
    setSaving(false);
    if (editRecipeId) {
      setStage("editor");
      setDraft(EMPTY);  // placeholder while we fetch
      let cancelled = false;
      api.recipesGet(editRecipeId).then((r) => {
        if (cancelled) return;
        const items = r.items.length > 0
          ? r.items.map((i) => ({ id: i.id, text: i.text, quantity: i.quantity || "" }))
          : [{ text: "", quantity: "" }];
        setDraft({
          name: r.recipe.name,
          imageUrl: r.recipe.imageUrl || null,
          instructions: Array.isArray(r.recipe.instructions) && r.recipe.instructions.length > 0
            ? r.recipe.instructions
            : [""],
          notes: r.recipe.notes || "",
          items,
          serves: r.recipe.serves != null ? String(r.recipe.serves) : "",
        });
        setOriginalItems(items);
      }).catch((e: any) => notify(e?.message || "couldn't load recipe"));
      return () => { cancelled = true; };
    } else {
      setStage("choose");
      setDraft(EMPTY);
      setOriginalItems([]);
    }
  }, [open, editRecipeId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const startScratch = () => {
    setDraft(EMPTY);
    setStage("editor");
  };

  const applyPreview = (preview: RecipeImportPreview) => {
    setDraft({
      name: preview.suggestedName || "",
      imageUrl: preview.imageUrl,
      instructions: preview.instructions.length > 0 ? preview.instructions : [""],
      notes: "",
      items: preview.items.length > 0
        ? preview.items.map((i) => ({ text: i.text, quantity: i.quantity }))
        : [{ text: "", quantity: "" }],
      serves: preview.serves != null ? String(preview.serves) : "",
    });
    setStage("editor");
    notify(`Imported ${preview.items.length} ingredient${preview.items.length === 1 ? "" : "s"}${preview.source ? ` · ${preview.source}` : ""}`);
  };

  const doImportUrl = async () => {
    const u = url.trim();
    if (!u) return;
    setImporting(true);
    setBlockedUrl(null);
    try {
      const preview = await api.recipesImport({ url: u });
      applyPreview(preview);
    } catch (e: any) {
      // "site blocked both the direct read and the headless retry" →
      // remember the URL, flip to the text tab, and surface an inline
      // banner there explaining what just happened (the toast alone
      // disappears too fast for the user to act on).
      const msg = String(e?.message || "import failed");
      if (/site blocked/i.test(msg) || /Paste text/i.test(msg)) {
        setBlockedUrl(u);
        setImportTab("text");
      } else {
        notify(msg);
      }
    } finally {
      setImporting(false);
    }
  };

  const doImportText = async () => {
    const t = pastedText.trim();
    if (!t) return;
    setImporting(true);
    try {
      const preview = await api.recipesImport({ text: t });
      applyPreview(preview);
    } catch (e: any) {
      notify(e?.message || "couldn't read that text");
    } finally {
      setImporting(false);
    }
  };

  const save = async () => {
    const name = draft.name.trim();
    if (!name) { notify("Give it a name first"); return; }
    const cleanedDraftItems = draft.items
      .map((i) => ({ id: i.id, text: i.text.trim(), quantity: i.quantity.trim() }))
      .filter((i) => i.text);
    if (cleanedDraftItems.length === 0) { notify("Add at least one ingredient"); return; }
    const cleanInstructions = draft.instructions.map((s) => s.trim()).filter(Boolean);
    const cleanNotes = draft.notes.trim();
    setSaving(true);
    // Parse + validate serves. Empty or non-numeric falls back to
    // null (clears any prior value); 1..99 inclusive otherwise.
    const servesNum = draft.serves.trim() === "" ? null : Number(draft.serves);
    const cleanServes = (servesNum != null && Number.isFinite(servesNum) && servesNum >= 1 && servesNum <= 99)
      ? Math.floor(servesNum)
      : null;
    try {
      if (editRecipeId) {
        // Edit mode: update the recipe row, then reconcile items.
        // Items with an id existed before; without id are new. Items
        // present in originalItems but not in the current draft (by
        // id) were deleted.
        await api.recipesUpdate(editRecipeId, {
          name,
          imageUrl: draft.imageUrl || null,
          instructions: cleanInstructions,
          notes: cleanNotes || null,
          serves: cleanServes,
        });
        const draftIds = new Set(cleanedDraftItems.filter((i) => i.id).map((i) => i.id!));
        const toDelete = originalItems.filter((i) => i.id && !draftIds.has(i.id));
        await Promise.all(toDelete.map((i) => api.recipesDeleteItem(i.id!)));
        // Sequentially handle additions/updates so server-side position
        // assignment stays in submission order.
        for (const it of cleanedDraftItems) {
          if (!it.id) {
            await api.recipesAddItem(editRecipeId, it.text, it.quantity || undefined);
            continue;
          }
          const orig = originalItems.find((o) => o.id === it.id);
          if (!orig) continue;
          if (orig.text !== it.text || orig.quantity !== it.quantity) {
            await api.recipesUpdateItem(it.id, { text: it.text, quantity: it.quantity });
          }
        }
        notify(`Updated · ${cleanedDraftItems.length} ingredient${cleanedDraftItems.length === 1 ? "" : "s"}`);
        onCreated(editRecipeId);  // reuse the callback to surface the same recipe
        onClose();
        return;
      }
      // Create mode (original behavior).
      const r = await api.recipesCreateWithItems({
        name,
        items: cleanedDraftItems.map((i) => ({ text: i.text, quantity: i.quantity })),
        imageUrl: draft.imageUrl || null,
        instructions: cleanInstructions.length > 0 ? cleanInstructions : null,
        serves: cleanServes,
      });
      // Notes can't go through createWithItems, so patch in a follow-up
      // when present. The classifier already fires on createWithItems
      // so this doesn't re-trigger it.
      if (cleanNotes) {
        try { await api.recipesUpdate(r.recipe.id, { notes: cleanNotes }); }
        catch { /* non-blocking — recipe is saved either way */ }
      }
      notify(`Saved · ${r.itemCount} ingredient${r.itemCount === 1 ? "" : "s"}`);
      onCreated(r.recipe.id);
      onClose();
    } catch (e: any) {
      notify(e?.message || "save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="nr-bg" onClick={onClose}>
      <div className="nr-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <header className="nr-head">
          {stage !== "choose" && !editRecipeId && (
            <button
              className="icon-btn"
              onClick={() => setStage("choose")}
              title="Back"
              type="button"
            >
              <Icon name="arrow-left" size={14} />
            </button>
          )}
          <span className="nr-head-title">
            {editRecipeId ? "Edit recipe"
              : stage === "choose" ? "New recipe"
              : stage === "url" ? "Import from URL"
              : "Review and save"}
          </span>
          <button className="icon-btn" onClick={onClose} title="Close" type="button">
            <Icon name="x" size={14} />
          </button>
        </header>

        {stage === "choose" && (
          <div className="nr-body">
            <p className="nr-sub">Two ways in. Pick whichever's quicker.</p>
            <div className="nr-choices">
              <button className="nr-tile" onClick={() => setStage("url")} type="button">
                <span className="nr-tile-icon"><Icon name="share" size={20} /></span>
                <div className="nr-tile-title">Import from URL</div>
                <p className="nr-tile-sub">Paste a recipe link and we'll pull the ingredients and steps for you.</p>
                <span className="nr-tile-tag">Recommended</span>
              </button>
              <button className="nr-tile" onClick={startScratch} type="button">
                <span className="nr-tile-icon"><Icon name="edit" size={20} /></span>
                <div className="nr-tile-title">Start from scratch</div>
                <p className="nr-tile-sub">Type the name, ingredients, and steps yourself. Good for family classics.</p>
              </button>
            </div>
          </div>
        )}

        {stage === "url" && (
          <div className="nr-body">
            <div className="nr-import-tabs" role="tablist">
              <button
                data-active={importTab === "url"}
                onClick={() => setImportTab("url")}
                type="button"
                role="tab"
              >
                <Icon name="share" size={13} />
                <span>Link</span>
              </button>
              <button
                data-active={importTab === "text"}
                onClick={() => setImportTab("text")}
                type="button"
                role="tab"
              >
                <Icon name="doc" size={13} />
                <span>Paste text</span>
              </button>
            </div>

            {importTab === "url" ? (
              <>
                <p className="nr-sub">
                  Paste a link from a cooking site. We'll grab the ingredients and steps so you can review and tweak before saving.
                </p>
                <div className="nr-url-row">
                  <input
                    type="url"
                    className="nr-input"
                    placeholder="https://…"
                    value={url}
                    onInput={(e) => setUrl((e.currentTarget as HTMLInputElement).value)}
                    onKeyDown={(e) => { if (e.key === "Enter") doImportUrl(); }}
                    autofocus
                  />
                  <button
                    className="btn btn-primary"
                    onClick={doImportUrl}
                    disabled={importing || !url.trim()}
                    type="button"
                  >
                    {importing ? <span>Importing…</span> : (
                      <>
                        <Icon name="sparkle" size={13} />
                        <span>Import</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="nr-supported">
                  <div className="nr-supported-label">Works well with</div>
                  <div className="nr-supported-list">
                    {SUPPORTED_HINTS.map((s) => <code key={s}>{s}</code>)}
                  </div>
                </div>
              </>
            ) : (
              <>
                {blockedUrl && (
                  <div className="nr-blocked-banner">
                    <div className="nr-blocked-icon">
                      <Icon name="warn" size={14} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="nr-blocked-title">That site blocks automated fetches.</div>
                      <div className="nr-blocked-sub">
                        Open <a href={blockedUrl} target="_blank" rel="noopener noreferrer">the page</a> in a new tab, copy the ingredients + steps, then paste below.
                      </div>
                    </div>
                    <button
                      className="icon-btn"
                      onClick={() => setBlockedUrl(null)}
                      title="Dismiss"
                      type="button"
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                )}
                {!blockedUrl && (
                  <p className="nr-sub">
                    Some sites (Allrecipes, Food Network, etc.) block automated reads. Copy the ingredients + steps from your browser and paste below — we'll extract the structured bits.
                  </p>
                )}
                <textarea
                  className="nr-input"
                  rows={10}
                  placeholder={"Paste ingredients and steps here…\n\ne.g.\n2 lb catfish fillets\n1 cup yellow cornmeal\n…\n\nHeat oil in a deep skillet to 365°F. Dredge fillets in cornmeal mix…"}
                  value={pastedText}
                  onInput={(e) => setPastedText((e.currentTarget as HTMLTextAreaElement).value)}
                  autofocus
                />
                <div className="nr-url-row" style={{ justifyContent: "flex-end" }}>
                  <button
                    className="btn btn-primary"
                    onClick={doImportText}
                    disabled={importing || !pastedText.trim()}
                    type="button"
                  >
                    {importing ? <span>Extracting…</span> : (
                      <>
                        <Icon name="sparkle" size={13} />
                        <span>Extract</span>
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            <button className="nr-skip" onClick={startScratch} type="button">
              Skip — I'll type it in
            </button>
          </div>
        )}

        {stage === "editor" && (
          <div className="nr-body nr-editor">
            <div className="nr-field">
              <label>Name</label>
              <input
                type="text"
                className="nr-input"
                value={draft.name}
                onInput={(e) => setDraft({ ...draft, name: (e.currentTarget as HTMLInputElement).value })}
                placeholder="Lemon pasta"
                autofocus
              />
            </div>

            {draft.imageUrl && (
              <div className="nr-field">
                <label>Cover image</label>
                <div className="nr-cover-preview">
                  <img src={draft.imageUrl} alt="" />
                  <button
                    className="btn-ghost"
                    type="button"
                    onClick={() => setDraft({ ...draft, imageUrl: null })}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            <div className="nr-field nr-field-serves">
              <label htmlFor="nr-serves">Serves</label>
              <input
                id="nr-serves"
                type="number"
                inputMode="numeric"
                min={1}
                max={99}
                className="nr-input nr-input-narrow"
                value={draft.serves}
                onInput={(e) => setDraft({ ...draft, serves: (e.currentTarget as HTMLInputElement).value })}
                placeholder="4"
              />
              <span className="nr-serves-hint">people — used together with your household size for auto-scale.</span>
            </div>

            <div className="nr-field">
              <label>Ingredients</label>
              <div className="nr-rows">
                {draft.items.map((it, i) => (
                  <div className="nr-row" key={i}>
                    <input
                      type="text"
                      className="nr-input"
                      value={it.text}
                      placeholder="Item"
                      onInput={(e) => setDraft({
                        ...draft,
                        items: draft.items.map((x, j) => j === i ? { ...x, text: (e.currentTarget as HTMLInputElement).value } : x),
                      })}
                    />
                    <input
                      type="text"
                      className="nr-input nr-qty"
                      value={it.quantity}
                      placeholder="qty"
                      onInput={(e) => setDraft({
                        ...draft,
                        items: draft.items.map((x, j) => j === i ? { ...x, quantity: (e.currentTarget as HTMLInputElement).value } : x),
                      })}
                    />
                    <button
                      className="nr-row-x"
                      type="button"
                      title="Remove"
                      onClick={() => setDraft({
                        ...draft,
                        items: draft.items.length === 1
                          ? [{ text: "", quantity: "" }]
                          : draft.items.filter((_, j) => j !== i),
                      })}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                ))}
                <button
                  className="nr-add-row"
                  type="button"
                  onClick={() => setDraft({ ...draft, items: [...draft.items, { text: "", quantity: "" }] })}
                >
                  <Icon name="plus" size={13} />
                  <span>Add ingredient</span>
                </button>
              </div>
            </div>

            <div className="nr-field">
              <label>Steps</label>
              <div className="nr-rows">
                {draft.instructions.map((step, i) => (
                  <div className="nr-row nr-row-step" key={i}>
                    <span className="nr-step-num">{i + 1}</span>
                    <textarea
                      rows={2}
                      className="nr-input"
                      value={step}
                      placeholder="What to do…"
                      onInput={(e) => setDraft({
                        ...draft,
                        instructions: draft.instructions.map((x, j) => j === i ? (e.currentTarget as HTMLTextAreaElement).value : x),
                      })}
                    />
                    <button
                      className="nr-row-x"
                      type="button"
                      title="Remove"
                      onClick={() => setDraft({
                        ...draft,
                        instructions: draft.instructions.length === 1
                          ? [""]
                          : draft.instructions.filter((_, j) => j !== i),
                      })}
                    >
                      <Icon name="x" size={12} />
                    </button>
                  </div>
                ))}
                <button
                  className="nr-add-row"
                  type="button"
                  onClick={() => setDraft({ ...draft, instructions: [...draft.instructions, ""] })}
                >
                  <Icon name="plus" size={13} />
                  <span>Add step</span>
                </button>
              </div>
            </div>

            <div className="nr-field">
              <label>Notes</label>
              <textarea
                rows={3}
                className="nr-input"
                value={draft.notes}
                placeholder="Optional — pan size, swap ideas, what the kids thought…"
                onInput={(e) => setDraft({
                  ...draft,
                  notes: (e.currentTarget as HTMLTextAreaElement).value,
                })}
              />
            </div>

            <div className="nr-actions">
              <button className="btn-ghost" onClick={onClose} type="button">Cancel</button>
              <button
                className="btn btn-primary"
                onClick={save}
                disabled={saving || !draft.name.trim()}
                type="button"
              >
                <Icon name="check" size={13} />
                <span>{saving ? "Saving…" : (editRecipeId ? "Save changes" : "Save recipe")}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
